import Page404 from '404'
import Loader from '@/components/ui/Loader'
import { fetcher } from 'api/swr'
import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useConfigStore as useSpineConfigStore } from 'AdminPanel/Spine/store/configStore'
import { useEditorStore as useSpineEditorStore } from 'AdminPanel/Spine/store/editorStore'
import { initialState, useEditorStore } from '../CanvasCore/uiSlice'
import { LayerPanel } from './BottomPanel/LayerPanel'
import ToningConfig from './BottomPanel/ToningConfig'
import CanvasPanel from './CanvasPanel'
import Nav from './Nav'
import RightPanel from './RightPanel'

export const useUI = create(
  devtools((set) => ({
    panel: 'pickerColor',
    lid: 'draft',
    toningId: null,
    currTab: 'info',
    dlc: null,
    canvasDrawRef: null,
    /** 混合预览人物 Konva 层（char），供 Sets / Overlay 导出用 */
    canvasCharDrawRef: null,
    /** 混合 Spine 预览导出所需：below/above Spine app+canvas、关联静态图层、Konva 背景/主体 ref */
    avatarSpineExport: null,
    togglePanel: (panel) => set({ panel }),
    toggleLayer: (lid) => set({ lid: lid ?? 'draft' }),
    toggleToning: (toningId) => set({ toningId, panel: 'pickerColor' }),
    toggleTab: (currTab) => set({ currTab }),
    toggleDlc: (dlc) => set({ dlc }),
    setCanvasDrawRef: (canvasDrawRef) => set({ canvasDrawRef }),
    setCanvasCharDrawRef: (canvasCharDrawRef) => set({ canvasCharDrawRef }),
    setAvatarSpineExport: (avatarSpineExport) => set({ avatarSpineExport }),
  })),
)

export const useSelectedColor = (tid, lid) => {
  const tonings = useEditorStore((state) => state.editors[tid]?.tonings)
  const toningId = useUI((state) => state.toningId)

  const selectedColor = useMemo(() => {
    return tonings?.[toningId]?.[lid ?? 'default']
  }, [tonings, toningId, lid])

  return selectedColor
}

const Editor = () => {
  const { tid } = useParams()
  const initEditor = useEditorStore((state) => state.initEditor)
  const tab = useUI((state) => state.currTab)

  useEffect(() => {
    if (tid) {
      initEditor(tid, initialState, false)
    }
  }, [tid, initEditor])

  const { data: template, isLoading } = useSWR([`/templates/${tid}`], fetcher)

  useEffect(() => {
    if (!tid || !template) return
    const {
      setAllRelatedTemplateIds,
      setLoadedRelatedTemplates,
      setTemplateType,
      setSpineData,
      setTemplateConfig,
    } = useSpineConfigStore.getState()
    setAllRelatedTemplateIds(template.relatedSpineTemplateIds || [])
    setLoadedRelatedTemplates([])
    setTemplateType('character')
    setSpineData(null)
    setTemplateConfig(null)
    useSpineEditorStore.getState().setSelectedSkins([])
  }, [tid, template?.relatedSpineTemplateIds])

  if (isLoading) return <Loader>Load template...</Loader>
  if (!template) return <Page404 />

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <Nav />
      <main className="grid min-h-0 flex-1 grid-cols-2 gap-4 p-4">
        {/* 左侧：画布 + 组件列表，各占一半高度 */}
        <div className="flex min-h-0 flex-col gap-2">
          <div className="min-h-0 flex-1 overflow-hidden">
            <CanvasPanel template={template} />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border p-1">
            {tab === 'tonings' ? <ToningConfig /> : <LayerPanel />}
          </div>
        </div>

        {/* 右侧：铺满剩余高度，内部自行滚动 */}
        <div className="flex min-h-0 flex-col overflow-hidden">
          <RightPanel />
        </div>
      </main>
    </div>
  )
}

export default Editor
